import { type ReactElement } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { H2 } from '@fastnorth/kraken/components/typography';
import { useTranslation } from 'react-i18next';
import RaceListItem from './components/RaceListItem';
import { useApp } from '../../hooks/AppProvider';
import { useSession } from '../../hooks/SessionProvider';
import { RACEROSTER_CDN_BASE_URL } from '../../config/constants';
import Empty from '@fastnorth/kraken/components/empty';
import isDisabled from './utils/isDisabled';
import AdButlerBlock from '../../components/ad-butler-block/AdButlerBlock';
import { useResponsive } from '@fastnorth/kraken/components/page-layout';
import { ScrollToGrid } from '@fastnorth/kraken/components/containers';
import type { KarwaUrlParams } from '../types.ts';
import DisplayMessages from '../../components/display-messages/DisplayMessages';
import { getDisplayMessages } from '../../layout/utils/getDisplayMessages';
import useAppLocation from '../../hooks/useAppLocation.ts';
import { Div } from '@fastnorth/kraken/components/layout';

const Home = (): ReactElement | null => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa' });
  const navigate = useNavigate();
  const { eventUniqueCode } = useParams<KarwaUrlParams>();
  const { search } = useLocation();
  const location = useLocation();
  const [{ event }] = useApp();
  const [{ session }] = useSession();
  const { roles } = session || {};
  const races = event?.subEvents || [];
  const { isMobile } = useResponsive();
  const eid = event?.eid;
  const locationState = useAppLocation();

  // Get display messages for home page (event-level only, no subEvent context)
  const filteredDisplayMessages = getDisplayMessages(
    event || null,
    null, // No subEvent context on home page
    locationState
  );
  const showDisplayMessages = filteredDisplayMessages.length > 0;

  if (!races?.length) {
    return (
      <Empty
        heading={t('event.race_list.no_available_results_label')}
        description={t('event.race_list.no_available_results_description')}
        image={{
          src: `${RACEROSTER_CDN_BASE_URL}/assets/cerbicons/empty-collaborate.svg`,
          alt: ' ',
          variant: 'illustration',
        }}
      />
    );
  }

  if (races.length === 1) {
    navigate(`/events/${eventUniqueCode}/race/${races[0].id}${search}`, {});
  }

  return (
    <>
      {/* Display messages for home page */}
      {showDisplayMessages && <DisplayMessages displayMessages={filteredDisplayMessages} />}

      {eid && (
        <AdButlerBlock
          eventId={eid}
          url={
            isMobile
              ? 'https://servedbyadbutler.com/adserve/;ID=168344;size=320x50;setID=797118;type=json'
              : 'https://servedbyadbutler.com/adserve/;ID=168344;size=970x90;setID=797120;type=json'
          }
          className={'flex w-full justify-center'}
        />
      )}

      <Div>
        <H2 fontWeight={'bold'} size={'2xl'} color={'muted'} spacing={'0 0 6'}>
          {t('event.race_list.title')}
        </H2>

        <ScrollToGrid
          scrollProps={{ gap: '5', animateScroll: true, fullBleed: true }}
          gridProps={{ className: 'pb-6' }}>
          {races.map((race) => (
            <RaceListItem
              disabled={isDisabled(race, roles || [])}
              key={race.id}
              title={race.name}
              startTime={race?.settings?.scheduledStartTime as string}
              href={`${location.pathname}/race/${race.id}${location.search}`}
            />
          ))}
        </ScrollToGrid>
      </Div>

      {eid && (
        <AdButlerBlock
          eventId={eid}
          url={
            isMobile
              ? 'https://servedbyadbutler.com/adserve/;ID=168344;size=380x380;setID=797119;type=json'
              : ''
          }
          className={'flex w-full justify-center'}
        />
      )}
    </>
  );
};

export default Home;
